import { Injectable } from '@angular/core';
import {
	HttpContextToken,
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { NotificationService } from '../services/notification.service';
import { TokenRefreshService } from 'app/services/token-refresh.service';
import { UtilityService } from 'app/services/utility.service';
import { datadogRum } from '@datadog/browser-rum';
import Swal from 'sweetalert2';
import { SUPPRESS_FORBIDDEN_NOTIFICATION } from './suppress-forbidden-notification';

const AUTH_LOG_PREFIX = '[auth-debug]';
const PO_REFRESH_STALE_PREFIX = 'PO_REFRESHED_SINCE_LOAD';
const PO_REFRESH_STALE_FALLBACK_MESSAGE =
	'The Purchase Order linked to this form was updated by a Change Order. Refresh the form to see the latest values before continuing.';
export const NOT_AUTHORIZED_MESSAGE = 'You are not authorized to do this action.';

let poRefreshPromptOpen = false;

/**
 * True while the "Purchase Order Updated" prompt for a stale-PO 409 is showing. A caller whose
 * error handler opens its own SweetAlert checks this first: SweetAlert shows one popup at a time,
 * so opening another would replace the prompt and its Refresh button.
 */
export function isPoRefreshPromptOpen(): boolean {
	return poRefreshPromptOpen;
}

/**
 * Opt-in, default OFF: the caller receives the HttpErrorResponse for ANY status, and the interceptor
 * shows no toast for a 403.
 *
 * This interceptor answers every error with a MESSAGE: a toast plus a rethrown string for a 403, a
 * rethrown string for everything else. That is right for the great majority of screens, where a
 * refused action is an interruption rather than a state the page is designed to be in. It is wrong
 * for a screen whose refusal IS a state, and it loses information either way: by the time the
 * subscriber runs there is no status to branch on and no response body to read, so an in-page "you
 * may not see this" panel is unreachable and a 422 that named which rule was broken arrives as the
 * characters "422".
 *
 * Set the token on a request and the interceptor rethrows the HttpErrorResponse instead of a
 * message, and shows no toast for the 403 the caller is about to render itself. Everything else is
 * unchanged, Datadog reporting included, and every request that says nothing behaves exactly as it
 * did, which is why the default is false rather than a list of exempt URLs.
 *
 * OPTING IN MEANS OWNING EVERY STATUS, not only the one you opted in for. The toast this
 * interceptor used to show is gone for the whole request, so a caller that opts in to read a 422
 * body also inherits the 403, the 500 and anything else that endpoint can answer, and each one now
 * reaches its subscriber as a raw response rather than as a sentence. A caller that maps only the
 * status it cared about and hands the rest to a toast renders the status code at the user, which is
 * strictly worse than the message it replaced. Map every status the endpoint can produce, or leave
 * the token off.
 */
export const HANDLES_ITS_OWN_ERRORS = new HttpContextToken<boolean>(() => false);

/**
 * Endpoints that establish or end a session. A 401 from any of them is the answer, not a signal
 * to go and refresh: refreshing in response to a failed login would loop.
 */
const SESSION_ENDPOINTS = [
	'api/authentication/login',
	'api/authentication/mslogin',
	'api/authentication/2fa',
	'api/authentication/refresh-access-token',
	'api/authentication/logout',
	// Refusing to impersonate someone says nothing about the admin's own session, so refreshing
	// and replaying only delays the error the caller needs to see.
	'api/authentication/secret-login-request',
];

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
	constructor(
		private notificationServices: NotificationService,
		private tokenRefreshService: TokenRefreshService,
		private utilityService: UtilityService
	) {}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (typeof navigator !== 'undefined' && !navigator.onLine) {
			const offlineMessage = 'You appear to be offline. Please check your internet connection and try again.';
			datadogRum.addError(new Error(offlineMessage), {
				url: request.url,
				requestMethod: request.method,
				errorType: 'request-failed',
				progressEventType: 'offline-precheck',
				clientOnline: false,
			});
			this.notificationServices.showError(offlineMessage, 'Connection Error');
			return throwError(() => offlineMessage);
		}
		return next.handle(request).pipe(
			catchError((err) => {
				// Checked before the Blob branch below. A request that asked for a Blob response
				// carries its error as a Blob too, and handling that first meant a 401 on any
				// download or report never reached the refresh path and simply failed.
				if (this.shouldAttemptRefresh(err, request)) {
					console.warn(`${AUTH_LOG_PREFIX} 401 received — entering refresh path`, { url: request.url });
					return this.retryAfterRefresh(request, next);
				}

				if (err instanceof HttpErrorResponse && err.error instanceof Blob && err.error.type === 'application/json') {
					// https://github.com/angular/angular/issues/19888
					// When request of type Blob, the error is also in Blob instead of object of the json data
					return new Promise<any>((resolve, reject) => {
						let reader = new FileReader();
						reader.onload = (e: Event) => {
							try {
								const errmsg = JSON.parse((<any>e.target).result);
								reject(
									new HttpErrorResponse({
										error: errmsg,
										headers: err.headers,
										status: err.status,
										statusText: err.statusText,
										url: err.url || undefined,
									})
								);
							} catch (e) {
								reject(err);
							}
						};
						reader.onerror = (e) => {
							reject(err);
						};
						reader.readAsText(err.error);
					});
				}

				if (err.status === 403) {
					if (request.context.get(HANDLES_ITS_OWN_ERRORS)) {
						return throwError(() => err);
					}

					if (!request.context.get(SUPPRESS_FORBIDDEN_NOTIFICATION)) {
						this.notificationServices.showError(NOT_AUTHORIZED_MESSAGE, 'Not Authorized.');
					}
					return throwError(() => NOT_AUTHORIZED_MESSAGE);
				}

				if (err.status === 409 && this.isPoRefreshStaleError(err)) {
					const message = this.poRefreshStaleMessage(err);
					this.promptPoRefreshReload(message);
					return throwError(() => message);
				}

				let error = this.utilityService.getErrorMessage(err);
				const progressEvent: ProgressEvent | null =
					typeof ProgressEvent !== 'undefined' && err?.error instanceof ProgressEvent
						? (err.error as ProgressEvent)
						: null;
				const rumError = new Error(typeof error === 'string' ? error : String(error));
				const xhrTarget = progressEvent?.target as XMLHttpRequest | null;
				const apiError = !progressEvent && err?.error && typeof err.error === 'object' ? err.error : null;
				datadogRum.addError(rumError, {
					url: err?.url ?? request.url,
					status: err?.status,
					statusText: err?.statusText,
					errorType: progressEvent ? 'request-failed' : 'api',
					progressEventType: progressEvent?.type,
					bytesLoaded: progressEvent?.loaded ?? null,
					bytesTotal: progressEvent?.total ?? null,
					lengthComputable: progressEvent?.lengthComputable ?? null,
					xhrResponseUrl: xhrTarget?.responseURL ?? null,
					xhrReadyState: xhrTarget?.readyState ?? null,
					requestMethod: request.method,
					httpErrorMessage: err?.message,
					serverMessage: apiError?.message ?? null,
					serverTraceId: apiError?.traceId ?? null,
					clientOnline: typeof navigator !== 'undefined' ? navigator.onLine : null,
				});
				// The report above is unconditional; only the SHAPE handed to the caller changes. A
				// caller that opted in reads the body, and NotificationService.showError runs the same
				// getErrorMessage over whatever it is given, so a response that reaches a toast still
				// renders the sentence this line would have produced.
				return throwError(() => (request.context.get(HANDLES_ITS_OWN_ERRORS) ? err : error));
			})
		);
	}

	private shouldAttemptRefresh(err: unknown, request: HttpRequest<any>): boolean {
		if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
			return false;
		}
		const url = err.url ?? request.url ?? '';
		return !SESSION_ENDPOINTS.some((endpoint) => url.includes(endpoint));
	}

	/**
	 * Replays the request once behind a completed refresh. A replay that 401s again is not
	 * retried: it falls through to the normal error path, and the refresh service has already
	 * ended the session if the token was refused.
	 */
	private retryAfterRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return this.tokenRefreshService.refresh().pipe(
			switchMap((token) => next.handle(this.addTokenHeader(request, token))),
			catchError((refreshError) => throwError(() => refreshError))
		);
	}

	private addTokenHeader(request: HttpRequest<any>, token: string) {
		return request.clone({ headers: request.headers.set('Authorization', `Bearer ${token}`) });
	}

	private isPoRefreshStaleError(err: HttpErrorResponse): boolean {
		const message = err?.error?.message;
		return typeof message === 'string' && message.startsWith(PO_REFRESH_STALE_PREFIX);
	}

	private poRefreshStaleMessage(err: HttpErrorResponse): string {
		const serverMessage = (err.error.message as string)
			.slice(PO_REFRESH_STALE_PREFIX.length)
			.replace(/^:\s*/, '')
			.trim();
		return serverMessage || PO_REFRESH_STALE_FALLBACK_MESSAGE;
	}

	private promptPoRefreshReload(message: string): void {
		// A save can fail with several stale-PO 409s in quick succession; re-firing
		// would replace the visible dialog and make the first Cancel appear ignored.
		if (poRefreshPromptOpen) {
			return;
		}
		poRefreshPromptOpen = true;
		Swal.fire({
			title: 'Purchase Order Updated',
			text: message,
			icon: 'warning',
			confirmButtonText: 'Refresh',
			showCancelButton: true,
			cancelButtonText: 'Cancel',
			customClass: {
				confirmButton: 'btn btn-success',
				cancelButton: 'btn btn-danger',
			},
			buttonsStyling: false,
		}).then((result) => {
			poRefreshPromptOpen = false;
			if (result.isConfirmed) {
				location.reload();
			}
		});
	}
}
